import React, { useState, useEffect, useMemo, memo } from 'react';
import { View, Text } from 'react-native';
import { CourseEntityInput } from '@oolio-group/domain';
import { useTranslation } from '@oolio-group/localization';
import { UilBoltAlt } from '@oolio-group/react-native-unicons';
import { useColors } from '../../hooks/useColors';
import CourseCallHeaderStyles from './CourseCallHeader.styles';
import { format } from 'date-fns';

const safeNormalizeEpochMs = (timestamp: number | undefined): number => {
  if (!timestamp || timestamp <= 0) {
    return Date.now();
  }

  return timestamp < 1e11 ? timestamp * 1000 : timestamp;
};

const TimerDisplay = memo(
  ({
    courseCallInfo,
    isCompleted,
    areAllItemsBumped,
    styles,
  }: {
    courseCallInfo?: CourseEntityInput['callInfo'];
    isCompleted: boolean;
    areAllItemsBumped: boolean;
    styles: any;
  }) => {
    const [elapsedTime, setElapsedTime] = useState<string>('');

    useEffect(() => {
      if (!courseCallInfo?.isCalled || !courseCallInfo?.calledAt) {
        setElapsedTime('');
        return;
      }

      const updateTimer = () => {
        const now = Date.now();
        const calledAt = safeNormalizeEpochMs(courseCallInfo.calledAt);
        const diff = now - calledAt;

        if (diff < 0) {
          setElapsedTime('0:00');
          return;
        }

        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setElapsedTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);

      return () => clearInterval(interval);
    }, [courseCallInfo?.isCalled, courseCallInfo?.calledAt]);

    const displayTime = useMemo(() => {
      if (isCompleted || areAllItemsBumped) {
        return courseCallInfo?.calledAt
          ? format(courseCallInfo.calledAt, 'hh:mm a')
          : '';
      }
      return elapsedTime;
    }, [isCompleted, areAllItemsBumped, courseCallInfo?.calledAt, elapsedTime]);

    return (
      <Text style={[styles.title, styles.rightAlignedText]}>{displayTime}</Text>
    );
  },
);
TimerDisplay.displayName = 'TimerDisplay';

interface CourseCallHeaderProps {
  courseName: string;
  courseCallInfo?: CourseEntityInput['callInfo'];
  areAllItemsBumped?: boolean;
  isCompleted?: boolean;
}

const CourseCallHeader: React.FC<CourseCallHeaderProps> = ({
  courseName,
  courseCallInfo,
  areAllItemsBumped = false,
  isCompleted = false,
}) => {
  const { colors } = useColors();
  const { translate } = useTranslation();

  const isCalled = courseCallInfo?.isCalled;

  const styles = CourseCallHeaderStyles(isCalled);

  return (
    <View style={styles.container}>
      <View style={styles.name}>
        <Text style={styles.title}>{courseName}</Text>
        {isCalled && (
          <TimerDisplay
            courseCallInfo={courseCallInfo}
            isCompleted={isCompleted}
            areAllItemsBumped={areAllItemsBumped}
            styles={styles}
          />
        )}
      </View>
      <View style={styles.accessory}>
        <Text style={styles.subtitle}>
          {isCalled
            ? translate('kitchenDisplay.away').toUpperCase()
            : translate('kitchenDisplay.hold').toUpperCase()}
        </Text>
        {isCalled && <UilBoltAlt size={18} color={colors.white} />}
      </View>
    </View>
  );
};

export default CourseCallHeader;
